import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useT } from '../i18n';
import XpnsrPageHeader from './XpnsrPageHeader';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import {
  BookOpen,
  Search,
  ChevronRight,
  ArrowUp,
} from 'lucide-react';

// Section ids are language-independent so the sidebar and in-document links
// work for every translated guide. Labels are English i18n keys — wrapped with
// t() at the render site (module scope has no hook access).
const TOC_ITEMS = [
  { id: 'section-1', label: 'What is XPNSR Tracker' },
  { id: 'section-2', label: 'Quick start' },
  { id: 'section-3', label: 'Traffic sources' },
  { id: 'section-4', label: 'Offers' },
  { id: 'section-5', label: 'Landing pages' },
  { id: 'section-6', label: 'Pre-landings' },
  { id: 'section-7', label: 'Campaigns' },
  { id: 'section-8', label: 'Links & aliases' },
  { id: 'section-9', label: 'Streams' },
  { id: 'section-10', label: 'Domains' },
  { id: 'section-11', label: 'Postbacks' },
  { id: 'section-12', label: 'Macros' },
  { id: 'section-13', label: 'Redirect modes' },
  { id: 'section-14', label: 'A/B tests' },
  { id: 'section-15', label: 'Anti-fraud' },
  { id: 'section-16', label: 'Analytics' },
  { id: 'section-17', label: 'CAPI' },
  { id: 'section-18', label: 'Automation' },
  { id: 'section-19', label: 'Team' },
  { id: 'section-20', label: 'API keys' },
  { id: 'section-21', label: 'Glossary' },
  { id: 'section-22', label: 'FAQ' },
  { id: 'section-23', label: 'Partners & lead forms' },
  { id: 'section-24', label: 'Google Ads & conversions' },
  { id: 'section-25', label: 'Affiliate networks' },
  { id: 'section-26', label: 'Reports (Report Builder)' },
  { id: 'section-27', label: 'Cost, integrations & ops' },
  { id: 'section-28', label: 'Archive & restore' },
  { id: 'section-29', label: 'Access control' },
  { id: 'section-30', label: 'Diagnostics' },
  { id: 'section-31', label: 'Plans & billing' },
  { id: 'section-32', label: 'Interface: search, shortcuts and language' },
];

function sectionId(text) {
  const match = String(text || '').match(/^\s*(\d+)\./);
  return match ? `section-${match[1]}` : '';
}

// Heading anchor exactly as GitLab builds it (lowercase, punctuation dropped,
// spaces to hyphens, repeated hyphens squeezed), so the in-guide links work both
// in the repository and here, in every language.
function headingSlug(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}_\- ]/gu, '')
    .replace(/ /g, '-')
    .replace(/-+/g, '-');
}

//: Languages with a translated in-app guide in public/; any other locale reads the English one.
const GUIDE_LOCALES = ['ru', 'es', 'pt', 'de', 'fr', 'it', 'pl', 'tr', 'uk'];

function localizedGuide(locale) {
  if (GUIDE_LOCALES.includes(locale)) return `/USER_GUIDE.${locale}.md`;
  return '/USER_GUIDE.md';
}

export default function Documentation() {
  const { t, locale } = useT();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMarkdown('');
    setSearch('');
    setActiveSection('');

    fetch(localizedGuide(locale), { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) throw new Error(`Documentation HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setMarkdown(text);
      })
      .catch(() => {
        if (!cancelled) {
          setMarkdown(`# ${t('Error')}\n\n${t('Failed to load documentation.')}`);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [locale, t]);

  useEffect(() => {
    if (!contentRef.current) return;
    const headings = contentRef.current.querySelectorAll('h2[id]');
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [markdown]);

  // Sub-heading anchors: h3/h4 get the GitLab heading id (numbered like GitLab
  // when a title repeats), so links such as #creating-a-campaign-step-by-step work.
  // Step anchors inside a section: "### Step 3." (any language: a word, then the
  // number) becomes id "section-23-step-3", so screens can link to the exact step.
  // A #hash in the URL scrolls there once the guide has rendered.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || loading || !markdown) return;
    let current = '';
    const seen = {};
    root.querySelectorAll('h1, h2, h3, h4').forEach((h) => {
      const base = headingSlug(h.textContent || '');
      const n = seen[base] || 0;
      seen[base] = n + 1;
      if (h.tagName === 'H2') { current = h.id || ''; return; }
      if (h.tagName === 'H1') return;
      const m = h.tagName === 'H3' ? (h.textContent || '').match(/^\s*\S+\s+(\d+)[.:]/) : null;
      h.id = current && m ? current + '-step-' + m[1] : (n ? base + '-' + n : base);
      h.classList.add('scroll-mt-20');
    });
    const hash = decodeURIComponent((window.location.hash || '').slice(1));
    if (hash) {
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ block: 'start' }), 60);
    }
  }, [markdown, loading]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredToc = search
    ? TOC_ITEMS.filter((item) =>
        t(item.label).toLowerCase().includes(search.toLowerCase())
      )
    : TOC_ITEMS;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">
          {t('Loading documentation...')}
        </div>
      </div>
    );
  }

  return (
    <div className="xp-page">
      <XpnsrPageHeader
        kicker={t('Help')}
        title={<>{t('Documentation')}</>}
        subtitle={'// ' + t('Step-by-step guide for every feature')}
      />

      <div className="flex gap-6 mt-6 relative">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-20">
            <Card>
              <CardContent className="p-4">
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('Search sections...')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-9 text-sm"
                  />
                </div>
                <nav className="space-y-0.5 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {filteredToc.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${
                        activeSection === item.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <ChevronRight className={`h-3 w-3 flex-shrink-0 transition-transform ${
                        activeSection === item.id ? 'rotate-90' : ''
                      }`} />
                      {t(item.label)}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0" ref={contentRef}>
          <Card>
            <CardContent className="p-6 md:p-8 prose prose-sm dark:prose-invert max-w-none docs-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={Object.fromEntries([
                  ['h1', ({ children }) => (
                    <h1 className="text-3xl font-bold mb-6 pb-3 border-b border-border">
                      {children}
                    </h1>
                  )],
                  ['h2', ({ children }) => {
                    const text = typeof children === 'string'
                      ? children
                      : Array.isArray(children)
                        ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
                        : '';
                    const id = sectionId(text);
                    return (
                      <h2 id={id} className="text-2xl font-bold mt-10 mb-4 pb-2 border-b border-border scroll-mt-20">
                        {children}
                      </h2>
                    );
                  }],
                  ['h3', ({ children }) => (
                    <h3 className="text-xl font-semibold mt-8 mb-3">{children}</h3>
                  )],
                  ['table', ({ children }) => (
                    <div className="overflow-x-auto my-4">
                      <table className="w-full text-sm border-collapse border border-border rounded-lg">{children}</table>
                    </div>
                  )],
                  ['th', ({ children }) => (
                    <th className="border border-border bg-muted/50 px-3 py-2 text-left font-semibold">{children}</th>
                  )],
                  ['td', ({ children }) => (
                    <td className="border border-border px-3 py-2">{children}</td>
                  )],
                  ['code', ({ inline, children }) => inline ? (
                    <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-primary">{children}</code>
                  ) : (
                    <pre className="bg-muted/50 border border-border rounded-lg p-4 overflow-x-auto my-4">
                      <code className="text-sm font-mono">{children}</code>
                    </pre>
                  )],
                  ['blockquote', ({ children }) => (
                    <blockquote className="border-l-4 border-primary/50 pl-4 my-4 text-muted-foreground italic">{children}</blockquote>
                  )],
                  ['a', ({ href, children }) => {
                    const sectionMatch = href?.match(/^#(\d+)-/);
                    const localizedHref = sectionMatch ? `#section-${sectionMatch[1]}` : href;
                    return (
                      <a
                        href={localizedHref}
                        className="text-primary hover:underline"
                        target={href?.startsWith('http') ? '_blank' : undefined}
                        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {children}
                      </a>
                    );
                  }],
                  ['hr', () => <hr className="my-8 border-border" />],
                  ['strong', ({ children }) => (
                    <strong className="font-semibold text-foreground">{children}</strong>
                  )],
                ])}

              >
                {markdown}
              </ReactMarkdown>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:bg-primary/90 transition-colors"
        aria-label={t('Back to top')}
        title={t('Back to top')}
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  );
}
